import { useCheckoutTracking } from '@checkout/core'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { buildAirwallexBilling } from './billing'
import { useAirwallex } from './Provider'
import styles from './SplitCardForm.module.css'
import { Skeleton } from '../../components'
import { withRetry } from '../../utils'

import type { AirwallexElement, ConfirmOptions } from './Provider'
import type { CheckoutPayerInput, PaymentInteractionOutcome } from '@checkout/core'

export interface SplitCardFormRef {
  submit: () => Promise<void>
}

interface FieldState {
  complete: boolean
  error?: string
}

interface ChangeDetail {
  complete?: boolean
  empty?: boolean
  error?: string | { message?: string }
  cardBin?: string
}

export interface SplitCardFormProps {
  intentId: string
  clientSecret: string
  currency: string
  payer?: CheckoutPayerInput
  onOutcome: (outcome: PaymentInteractionOutcome) => void
  onCardChange?: (cardBin: string) => void
  paymentError?: string | null
  onValidityChange?: (isValid: boolean) => void
  className?: string
  isSubscription?: boolean
}

const ELEMENT_RETRY_CONFIG = {
  maxRetries: 3,
  timeout: 10000,
  baseDelay: 1000,
  maxDelay: 3000,
}

const ELEMENT_STYLE = {
  base: {
    fontSize: '16px',
    color: '#333333',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    '::placeholder': { color: '#9ca3af' },
  },
  invalid: { color: '#dc2626' },
}

function extractFieldState(event: unknown): FieldState {
  const detail = (event as CustomEvent<ChangeDetail>).detail
  const error =
    typeof detail?.error === 'string' ? detail.error : detail?.error?.message || undefined
  return { complete: detail?.complete === true, error }
}

export const SplitCardForm = forwardRef<SplitCardFormRef, SplitCardFormProps>(
  function SplitCardForm(
    {
      intentId,
      clientSecret,
      currency: _currency,
      payer,
      onOutcome,
      onCardChange,
      paymentError,
      onValidityChange,
      className,
      isSubscription = true,
    },
    ref
  ) {
    const {
      createElement,
      isReady,
      isLoading: sdkLoading,
      error: sdkError,
      retry: retrySDK,
    } = useAirwallex()
    const { trackPayment } = useCheckoutTracking()
    const cardNumberRef = useRef<AirwallexElement | null>(null)
    const expiryRef = useRef<AirwallexElement | null>(null)
    const cvcRef = useRef<AirwallexElement | null>(null)
    const cardBinRef = useRef<string>()
    const mountedRef = useRef(false)
    const [cardNumberState, setCardNumberState] = useState<FieldState>({ complete: false })
    const [expiryState, setExpiryState] = useState<FieldState>({ complete: false })
    const [cvcState, setCvcState] = useState<FieldState>({ complete: false })
    const [readyCount, setReadyCount] = useState(0)
    const [processing, setProcessing] = useState(false)
    const [isCreating, setCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isElementReady = readyCount >= 3
    const isValid =
      isElementReady && cardNumberState.complete && expiryState.complete && cvcState.complete

    useEffect(() => {
      onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    const cleanupElements = useCallback(() => {
      for (const elementRef of [cardNumberRef, expiryRef, cvcRef]) {
        if (!elementRef.current) {
          continue
        }
        elementRef.current.unmount()
        elementRef.current = null
      }
      mountedRef.current = false
    }, [])

    const initializeElements = useCallback(async () => {
      if (!createElement) {
        return
      }
      cleanupElements()
      setCreating(true)
      setError(null)
      setReadyCount(0)
      try {
        const [cardNumber, expiry, cvc] = await Promise.all([
          withRetry(
            () =>
              createElement('cardNumber', {
                style: ELEMENT_STYLE,
                placeholder: 'Card number',
              }),
            ELEMENT_RETRY_CONFIG
          ),
          withRetry(
            () => createElement('expiry', { style: ELEMENT_STYLE, placeholder: 'MM / YY' }),
            ELEMENT_RETRY_CONFIG
          ),
          withRetry(
            () => createElement('cvc', { style: ELEMENT_STYLE, placeholder: 'CVC' }),
            ELEMENT_RETRY_CONFIG
          ),
        ])

        cardNumber.mount('airwallex-card-number')
        expiry.mount('airwallex-expiry')
        cvc.mount('airwallex-cvc')

        cardNumber.on('ready', () => setReadyCount((value) => value + 1))
        cardNumber.on('change', (event) => {
          setCardNumberState(extractFieldState(event))
          const detail = (event as CustomEvent<ChangeDetail>).detail
          if (detail?.cardBin && detail.cardBin !== cardBinRef.current) {
            cardBinRef.current = detail.cardBin
            onCardChange?.(detail.cardBin)
          } else if (detail?.empty) {
            cardBinRef.current = undefined
          }
        })
        expiry.on('ready', () => setReadyCount((value) => value + 1))
        expiry.on('change', (event) => setExpiryState(extractFieldState(event)))
        cvc.on('ready', () => setReadyCount((value) => value + 1))
        cvc.on('change', (event) => setCvcState(extractFieldState(event)))

        cardNumberRef.current = cardNumber
        expiryRef.current = expiry
        cvcRef.current = cvc
        mountedRef.current = true
      } catch (initializationError) {
        setError(
          initializationError instanceof Error
            ? initializationError.message
            : 'Failed to initialize card elements'
        )
      } finally {
        setCreating(false)
      }
    }, [cleanupElements, createElement, onCardChange])

    useEffect(() => {
      if (!isReady || !createElement || mountedRef.current) {
        return
      }
      void initializeElements()
      return cleanupElements
    }, [cleanupElements, createElement, initializeElements, isReady])

    useEffect(() => {
      if (isElementReady) {
        trackPayment('card_form_ready')
      }
    }, [isElementReady, trackPayment])

    const submit = useCallback(async () => {
      if (!cardNumberRef.current || !isValid || processing) {
        return
      }
      setProcessing(true)
      setError(null)
      trackPayment('payment_submit_start', { payment_method: 'card' })
      try {
        const options: ConfirmOptions = {
          intent_id: intentId,
          client_secret: clientSecret,
        }
        const billing = buildAirwallexBilling(payer)
        if (billing) {
          options.payment_method = { billing }
        }
        if (isSubscription) {
          options.payment_consent = {
            next_triggered_by: 'merchant',
            merchant_trigger_reason: 'scheduled',
          }
        }

        const result = await cardNumberRef.current.confirm(options)
        if (result.status === 'SUCCEEDED' || result.status === 'REQUIRES_CAPTURE') {
          trackPayment('payment_submit_success')
          onOutcome({ status: 'succeeded', provider: 'airwallex', pmId: 'card', raw: result })
          return
        }
        if (result.status === 'PENDING' || result.status === 'REQUIRES_CUSTOMER_ACTION') {
          onOutcome({ status: 'processing', provider: 'airwallex', pmId: 'card', raw: result })
          return
        }
        const message = result.error?.message || `Payment status: ${result.status}`
        setError(message)
        trackPayment('payment_submit_error', {
          error_message: message,
          error_code: result.error?.code,
        })
        onOutcome({
          status: 'failed',
          provider: 'airwallex',
          pmId: 'card',
          error: message,
          errorCode: result.error?.code,
          raw: result,
        })
      } catch (paymentError) {
        const message = paymentError instanceof Error ? paymentError.message : 'Payment failed'
        setError(message)
        trackPayment('payment_submit_error', { error_message: message })
        onOutcome({
          status: 'failed',
          provider: 'airwallex',
          pmId: 'card',
          error: message,
          raw: paymentError,
        })
      } finally {
        setProcessing(false)
      }
    }, [
      clientSecret,
      intentId,
      isSubscription,
      isValid,
      onOutcome,
      payer,
      processing,
      trackPayment,
    ])

    useImperativeHandle(ref, () => ({ submit }), [submit])

    const displayError = sdkError || error || paymentError
    const fieldError = cardNumberState.error || expiryState.error || cvcState.error
    return (
      <div className={`${styles.form} ${className ?? ''}`}>
        {!isElementReady && (
          <div className={styles.skeletonContainer}>
            <Skeleton
              height="var(--checkout-layout-input-height)"
              style={{ borderRadius: 'var(--checkout-border-radius-md)' }}
            />
            <Skeleton
              height="var(--checkout-layout-input-height)"
              style={{ borderRadius: 'var(--checkout-border-radius-md)' }}
            />
          </div>
        )}
        <div
          className={styles.cardFieldsContainer}
          style={{ display: isElementReady ? 'grid' : 'none' }}
        >
          <div
            id="airwallex-card-number"
            className={`${styles.cardNumberWrapper} ${cardNumberState.error ? styles.hasError : ''}`}
          />
          <div className={styles.row}>
            <div
              id="airwallex-expiry"
              className={`${styles.expiryWrapper} ${expiryState.error ? styles.hasError : ''}`}
            />
            <div
              id="airwallex-cvc"
              className={`${styles.cvcWrapper} ${cvcState.error ? styles.hasError : ''}`}
            />
          </div>
        </div>
        {fieldError && isElementReady && <div className={styles.fieldError}>{fieldError}</div>}
        {displayError && (
          <div className={styles.errorContainer}>
            <span className={styles.error}>{displayError}</span>
            {sdkError && !sdkLoading && !processing && !isCreating && (
              <button
                type="button"
                className={styles.retryButton}
                onClick={retrySDK}
                data-ga-action-name="init_retry_click"
                data-ga-module="payment_card"
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    )
  }
)

SplitCardForm.displayName = 'SplitCardForm'
